// src/routes/_layout/writing/$writingId.tsx
import "@/styles/_variables.scss"
import "@/styles/_keyframe-animations.scss"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import React from "react"

import {
  Box,
  Flex,
  Icon,
} from "@chakra-ui/react"
import { FiSidebar } from "react-icons/fi"

import { MetaHeader } from "@/components/Writings/MetaHeader"
import { MenuBar } from "@/components/Writings/MenuBar"

import { WritingVersionPanel } from "@/components/Writings/WritingVersionPannel"

import { SimpleEditor } from "@/components/tiptap/tiptap-templates/simple/simple-editor"
import { createFileRoute } from "@tanstack/react-router"
import { WritingsService } from "@/client"
import type { Editor } from "@tiptap/react"

export const Route = createFileRoute("/_layout/writing/$writingId")({
  component: Writing,
  loader: ({ params }) => params,
})

function Writing() {
  const { writingId } = Route.useParams()
  const [editor, setEditor] = useState<Editor | null>(null)
  const [showVersionPanel, setShowVersionPanel] = useState(true)
  const [_lastSavedTime, setLastSavedTime] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [_isSaving, setIsSaving] = useState(false)

  const { data: writing, isLoading } = useQuery({
    queryKey: ["writing", writingId],
    queryFn: () => WritingsService.readWriting({ id: writingId }),
  })

  React.useEffect(() => {
    if (writing?.content_updated_at) {
      setLastSavedTime(new Date(writing.content_updated_at).toLocaleTimeString())
    }
  }, [writing?.content_updated_at])

  const queryClient = useQueryClient()

  const saveContentMutation = useMutation({
    mutationFn: async (updatedContent: any) => {
      return await WritingsService.updateWritingContent({
        id: writingId,
        requestBody: { content: updatedContent },
      })
    },
    onSuccess: (data) => {
      const updatedAt = new Date(data.content_updated_at).toLocaleTimeString()
      setLastSavedTime(updatedAt)

      queryClient.setQueryData(["writing", writingId], (old: Awaited<ReturnType<typeof WritingsService.readWriting>> | undefined) => {
        return old
          ? {
            ...old,
            content: editor?.getJSON(),
            content_updated_at: data.content_updated_at, // use real one
          }
          : old
      })
    }
  })

  React.useEffect(() => {
    if (!editor || !writing) return

    const original = JSON.stringify(writing.content)

    const handleUpdate = () => {
      const current = JSON.stringify(editor.getJSON())
      setIsDirty(current !== original)
    }

    editor.on("update", handleUpdate)

    return () => {
      editor.off("update", handleUpdate)
    }
  }, [editor, writing])

  React.useEffect(() => {
    if (!editor || !isDirty) return

    const timeout = setTimeout(() => {
      const json = editor.getJSON()
      setIsSaving(true)

      saveContentMutation.mutate(json, {
        onSuccess: (data) => {
          const updatedAt = new Date(data.content_updated_at).toLocaleTimeString()
          setLastSavedTime(updatedAt)
          setIsDirty(false)
        },
        onSettled: () => {
          setIsSaving(false)
        },
      })
    }, 5000) // debounce 3 seconds

    return () => clearTimeout(timeout)
  }, [editor, isDirty])

  if (isLoading || !writing) return null

  return (
    <Flex direction="column" height="100vh" width="100vw" bg="bg.option2" p={4}>
      {/* Row 1 */}
      <Box height="80px" flexShrink={0} width="100%" bg="gray.100" mt={-4}>
        <MetaHeader
          writing={writing}
          isSaving={_isSaving}
          isDirty={isDirty}
          lastSavedTime={_lastSavedTime}
          isSaveLoading={saveContentMutation.isPending}
        />
      </Box>

      <Box height="40px" flexShrink={0} width="100%" bg="bg.option2">
        <MenuBar editor={editor} />
      </Box>
      
      <Box flex="1" minHeight="0" width="100%" bg="#fdfdfd" overflow="hidden">
        <Flex direction="row" height="100%" position="relative">
          {/* Editor Area */}
          <Box flex="1" minWidth="0">
            <SimpleEditor
              initialContent={writing.content}
              onReady={(editorInstance) => setEditor(editorInstance)}
            />
          </Box>

          {/* Toggle Button between editor and panel */}
          <Box
            width="32px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="bg.option1"
            borderLeft="1px solid #e0e0e0"
            cursor="pointer"
            onClick={() => setShowVersionPanel(!showVersionPanel)}
            _hover={{ bg: "gray.100" }}
            zIndex="1"
          >
            <Icon as={FiSidebar} boxSize={5} />
          </Box>

          {/* Conditionally Render Panel */}
          {showVersionPanel && (
            <Box width="300px" flexShrink={0} borderLeft="1px solid #e0e0e0">
              <WritingVersionPanel writingId={writingId} editor={editor} />
            </Box>
          )}
        </Flex>
      </Box>
    </Flex>
  )
}

export default Writing
